import {useMemo, useState} from "react";
import {App as AntdApp, Empty, Popconfirm, Table, Tag, Tooltip} from "antd";
import className from "classnames/bind";
import {Button, ExportButtons, FontAwesomeIcon} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {useDeleteKeywordItemMutation, useGetKeywordItemsQuery, useAddKeywordItemsMutation} from "~/reduxs/api/keywordApiSlice";
import {useAddTrackedKeywordMutation} from "~/reduxs/api/gscApiSlice";
import {useGetMySubscriptionQuery} from "~/reduxs/api/subscriptionApiSlice";
import KeywordAddModal from "./KeywordAddModal";
import {metricsColumns, useMetricsLookup} from "./keywordMetrics";
import style from "../style/Keyword.module.scss";

const cn = className.bind(style);

/** Nhãn + màu tag theo nguồn từ khoá. */
const SOURCE_META = {
	gsc:        {label: 'GSC',      color: 'blue'},
	ai:         {label: 'AI',       color: 'purple'},
	content:    {label: 'Nội dung', color: 'cyan'},
	manual:     {label: 'Thủ công', color: 'default'},
	trends:     {label: 'Google Trends', color: 'volcano'},
	google_ads: {label: 'Google Ads', color: 'gold'},
};

const posText = (v) => (v === null || v === undefined ? '—' : Number(v).toFixed(1));

/**
 * Tab "Kho từ khoá": danh sách từ khoá đã lưu của dự án (mọi nguồn) + số liệu GSC hiện tại
 * (28 ngày gần nhất, BE đối chiếu sẵn). Thêm thủ công qua modal; xoá; ghim sang Theo dõi
 * thứ hạng (chỉ hiện khi gói có can_keyword_tracking). Trần kho theo gói max_keyword_items.
 */
function KeywordVault({projectId}) {

	const {notification} = AntdApp.useApp();
	const {data, isFetching} = useGetKeywordItemsQuery(projectId);
	const [addItems, {isLoading: adding}] = useAddKeywordItemsMutation();
	const [deleteItem] = useDeleteKeywordItemMutation();
	const [addTracked] = useAddTrackedKeywordMutation();
	const {data: sub} = useGetMySubscriptionQuery();

	const canTrack = !!sub?.features?.keyword_tracking;
	const [openAdd, setOpenAdd] = useState(false);

	const items = useMemo(() => data?.items || [], [data]);
	const max = data?.max || 0;

	// Volume/CPC/KD (DataForSEO §8): BE join sẵn cache chung vào items (0đ); nút tra xếp hàng
	// các từ cache miss (trừ quota tháng) rồi poll — kết quả mới merge đè theo keyword.
	const {run: runLookup, rows: lookupRows, lookingUp} = useMetricsLookup(projectId, notification);
	const canLookup = !!sub?.is_root || (sub?.keyword_lookups?.limit ?? 0) > 0;

	const resolveMetrics = (r) => {
		const looked = lookupRows[r.keyword];
		return {
			metrics: (looked?.metrics) || r.metrics || null,
			pending: looked?.status === 'pending',
		};
	};

	const handleAdd = async ({keywords, note}) => {
		const list = String(keywords || '')
			.split('\n')
			.map((s) => s.trim())
			.filter(Boolean)
			.map((kw) => ({keyword: kw, source: 'manual', ...(note?.trim() ? {note: note.trim()} : {})}));

		if (!list.length) return;

		try {
			const res = await addItems({projectId, items: list}).unwrap();
			if (res?.added > 0) {
				notification.success({message: 'Đã lưu vào kho', description: `${res.added} từ khoá mới${res.skipped ? ` (${res.skipped} đã có sẵn)` : ''}.`});
			} else {
				notification.info({message: 'Đã có', description: 'Các từ khoá này đã có trong kho.'});
			}
			setOpenAdd(false);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không lưu được từ khoá')});
		}
	};

	const handleDelete = async (row) => {
		try {
			await deleteItem({projectId, id: row.id}).unwrap();
			notification.success({message: 'Đã xoá khỏi kho', description: row.keyword});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không xoá được')});
		}
	};

	// Ghim sang Theo dõi thứ hạng (Rank Tracking của tab GSC) — dùng lại API sẵn có.
	const handleTrack = async (row) => {
		try {
			const res = await addTracked({projectId, keyword: row.keyword}).unwrap();
			if (res?.added > 0) {
				notification.success({message: 'Đã ghim theo dõi thứ hạng', description: row.keyword});
			} else {
				notification.info({message: 'Đã theo dõi', description: 'Từ khoá này đã có trong Theo dõi thứ hạng.'});
			}
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không ghim được từ khoá')});
		}
	};

	const columns = [
		{
			title: 'Từ khoá', dataIndex: 'keyword', key: 'keyword', ellipsis: true,
			render: (v, r) => (
				<span>
					{v}
					{r.intent && <Tag style={{marginLeft: 8}}>{r.intent}</Tag>}
					{r.tracked && (
						<Tooltip title="Đang theo dõi thứ hạng (tab Theo dõi thứ hạng của GSC)">
							<Tag color="gold" style={{marginLeft: 8}}>
								<FontAwesomeIcon icon="fa-light fa-chart-line" /> Theo dõi
							</Tag>
						</Tooltip>
					)}
				</span>
			),
		},
		{
			title: 'Nguồn', dataIndex: 'source', key: 'source', width: 100, align: 'center',
			filters: Object.entries(SOURCE_META).map(([value, m]) => ({text: m.label, value})),
			onFilter: (value, r) => r.source === value,
			render: (v) => {
				const m = SOURCE_META[v] || SOURCE_META.manual;
				return <Tag color={m.color}>{m.label}</Tag>;
			},
		},
		{
			title: 'Vị trí hiện tại', key: 'cur_position', align: 'center', width: 120,
			sorter: (a, b) => (a.current?.position ?? 999) - (b.current?.position ?? 999),
			render: (_, r) => r.current
				? (
					<Tooltip title={`${r.current.clicks} clicks · ${r.current.impressions} impressions (${r.current.from} → ${r.current.to})`}>
						<strong>{posText(r.current.position)}</strong>
					</Tooltip>
				)
				: <span style={{color: '#9ca3af'}}>—</span>,
		},
		{
			// Cột phụ — ẩn dưới 768px cho gọn mobile.
			title: 'Impressions hiện tại', key: 'cur_impr', align: 'center', width: 150, responsive: ['md'],
			sorter: (a, b) => (a.current?.impressions ?? -1) - (b.current?.impressions ?? -1),
			render: (_, r) => r.current ? r.current.impressions.toLocaleString('vi-VN') : <span style={{color: '#9ca3af'}}>—</span>,
		},
		// Volume / CPC / KD từ DataForSEO (cache chung — bấm "Tra volume" để lấy từ còn thiếu).
		...metricsColumns(resolveMetrics),
		{
			title: 'Lúc lưu', key: 'snapshot', align: 'center', width: 130, responsive: ['md'],
			render: (_, r) => (r.source === 'gsc' && r.impressions > 0)
				? <span className={cn('hint')}>{r.impressions.toLocaleString('vi-VN')} impr · vị trí {posText(r.position)}</span>
				: <span style={{color: '#9ca3af'}}>—</span>,
		},
		{title: 'Ghi chú', dataIndex: 'note', key: 'note', ellipsis: true, width: 160, responsive: ['md'], render: (v) => v || <span style={{color: '#9ca3af'}}>—</span>},
		{
			title: '', key: 'action', align: 'right', width: canTrack ? 110 : 60,
			render: (_, r) => (
				<span style={{display: 'inline-flex', gap: 6}}>
					{canTrack && !r.tracked && (
						<Tooltip title="Ghim sang Theo dõi thứ hạng">
							<Button small white onClick={() => handleTrack(r)}
								leftIcon={<FontAwesomeIcon icon="fa-light fa-chart-line" />} />
						</Tooltip>
					)}
					<Popconfirm okText="Xoá" cancelText="Huỷ" onConfirm={() => handleDelete(r)}
						title={r.tracked ? 'Từ khoá đang được theo dõi thứ hạng — xoá khỏi kho sẽ bỏ theo dõi luôn. Xoá?' : 'Xoá từ khoá này khỏi kho?'}>
						<Button small red leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />} />
					</Popconfirm>
				</span>
			),
		},
	];

	const buildExcel = () => [{
		name: 'Kho từ khoá',
		aoa: [
			['Từ khoá', 'Nguồn', 'Intent', 'Vị trí hiện tại', 'Impressions hiện tại', 'Volume', 'CPC ($)', 'KD', 'Impressions lúc lưu', 'Vị trí lúc lưu', 'Ghi chú', 'Ngày lưu'],
			...items.map((r) => {
				const m = resolveMetrics(r).metrics;
				return [
					r.keyword, (SOURCE_META[r.source] || SOURCE_META.manual).label, r.intent || '',
					r.current?.position ?? '', r.current?.impressions ?? '',
					m?.volume ?? '', m?.cpc ?? '', m?.kd ?? '',
					r.impressions || '', r.position ?? '', r.note || '', r.created || '',
				];
			}),
		],
	}];

	const buildPdf = () => ({
		title: 'Kho từ khoá của dự án',
		subtitle: `${items.length} từ khoá`,
		sections: [{
			columns: ['Từ khoá', 'Nguồn', 'Vị trí hiện tại', 'Impressions hiện tại', 'Ghi chú'],
			rows: items.map((r) => [
				r.keyword, (SOURCE_META[r.source] || SOURCE_META.manual).label,
				r.current?.position ?? '—', r.current?.impressions ?? '—', r.note || '',
			]),
		}],
	});

	return (
		<div>
			<div className={cn('actionBar')}>
				<div style={{display: 'inline-flex', alignItems: 'center', gap: 12}}>
					<Button primary small onClick={() => setOpenAdd(true)}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}>Thêm từ khoá</Button>
					<span className={cn('hint')}>{items.length}{max > 0 ? `/${max}` : ''} từ khoá</span>
				</div>
				<span style={{display: 'inline-flex', gap: 8, alignItems: 'center'}}>
					{canLookup && items.length > 0 && (
						<Tooltip title="Lấy volume/CPC/độ khó cho cả kho (tối đa 200/lần). Từ đã có sẽ KHÔNG tốn lượt.">
							<Button small white loading={lookingUp} onClick={() => runLookup(items.map((r) => r.keyword))}
								leftIcon={<FontAwesomeIcon icon="fa-light fa-chart-column" />}>
								Tra volume
							</Button>
						</Tooltip>
					)}
					<ExportButtons filename={`kho-tu-khoa-${projectId}`} buildExcel={buildExcel} buildPdf={buildPdf} />
				</span>
			</div>

			{items.length === 0 && !isFetching ? (
				<Empty description="Kho từ khoá trống. Lưu từ khoá từ tab Khám phá hoặc thêm thủ công." />
			) : (
				<Table
					rowKey="id"
					size="small"
					loading={isFetching}
					columns={columns}
					dataSource={items}
					scroll={{x: 'max-content'}}
					pagination={{pageSize: 20, hideOnSinglePage: true, showSizeChanger: false}}
				/>
			)}

			<KeywordAddModal open={openAdd} submitting={adding} onCancel={() => setOpenAdd(false)} onSubmit={handleAdd} />
		</div>
	);
}

export default KeywordVault;
